/**
 * Assistant / Daily Brief — the calm "what should I do now?" surface.
 *
 * Per the AI Assistant Security Contract, the brief is READ-ONLY and computed
 * on demand from existing Life OS state. It never stores its own truth, never
 * acts autonomously, and every item carries a reason code + a route to act.
 * `composeBrief` is a pure function so the prioritisation is deterministic and
 * testable; the data layer only gathers the inputs.
 */

export type BriefReason =
  | 'agenda'
  | 'capture_triage'
  | 'habit_due'
  | 'target_at_risk'
  | 'project_stalled'
  | 'problem_open'
  | 'daily_review'
  | 'all_clear';

export interface BriefItem {
  readonly reason: BriefReason;
  readonly text: string;
  /** Where the owner can act on this item. */
  readonly route: string;
}

export interface Brief {
  readonly date: string;
  readonly headline: string;
  readonly items: readonly BriefItem[];
}

export interface BriefInputs {
  readonly dateIso: string;
  readonly agendaCount: number;
  readonly nextAgendaTitle: string | null;
  readonly nextAgendaAt: string | null;
  readonly unreviewedCaptures: number;
  readonly habitsDue: readonly string[];
  readonly targetsAtRisk: readonly string[];
  readonly stalledProjects: readonly string[];
  readonly openProblems: number;
  readonly reviewedToday: boolean;
}

export const BRIEF_REASON_LABEL: Record<BriefReason, string> = {
  agenda: 'On the calendar',
  capture_triage: 'Inbox',
  habit_due: 'Habit',
  target_at_risk: 'Target',
  project_stalled: 'Project',
  problem_open: 'Repair',
  daily_review: 'Review',
  all_clear: 'Clear',
};

/** Keep the brief calm — never dump everything at once. */
export const MAX_BRIEF_ITEMS = 7;

function hhmm(iso: string): string {
  const d = new Date(iso);
  const h = String(d.getUTCHours()).padStart(2, '0');
  const m = String(d.getUTCMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return n === 1 ? one : many;
}

/**
 * Compose a prioritised, capped daily brief. Order reflects "what helps the
 * next decision": the next time-anchored thing, then habits due, then at-risk
 * targets, then inbox, then stalled work, then open repairs, then review.
 */
export function composeBrief(input: BriefInputs): Brief {
  const items: BriefItem[] = [];

  if (input.nextAgendaTitle && input.nextAgendaAt) {
    const more = input.agendaCount > 1 ? ` (+${input.agendaCount - 1} more today)` : '';
    items.push({
      reason: 'agenda',
      text: `Next at ${hhmm(input.nextAgendaAt)}: ${input.nextAgendaTitle}${more}`,
      route: '/calendar',
    });
  }

  for (const habit of input.habitsDue) {
    items.push({ reason: 'habit_due', text: `${habit} is due today`, route: '/routines' });
  }

  for (const target of input.targetsAtRisk) {
    items.push({ reason: 'target_at_risk', text: `${target} needs attention this period`, route: '/targets' });
  }

  if (input.unreviewedCaptures > 0) {
    items.push({
      reason: 'capture_triage',
      text: `Triage ${input.unreviewedCaptures} captured ${plural(input.unreviewedCaptures, 'item')}`,
      route: '/capture',
    });
  }

  for (const project of input.stalledProjects) {
    items.push({ reason: 'project_stalled', text: `${project} has no next action — decide one`, route: '/projects' });
  }

  if (input.openProblems > 0) {
    items.push({
      reason: 'problem_open',
      text: `${input.openProblems} open ${plural(input.openProblems, 'limiter')} in repair`,
      route: '/problems',
    });
  }

  if (!input.reviewedToday) {
    items.push({ reason: 'daily_review', text: 'Run a short end-of-day review', route: '/reviews' });
  }

  const capped = items.slice(0, MAX_BRIEF_ITEMS);

  if (capped.length === 0) {
    return {
      date: input.dateIso.slice(0, 10),
      headline: "You're clear.",
      items: [
        {
          reason: 'all_clear',
          text: 'Nothing is pressing. Capture anything on your mind, or rest — both are valid.',
          route: '/capture',
        },
      ],
    };
  }

  const headline =
    capped.length === 1 ? 'One thing to look at.' : `${capped.length} things worth your attention.`;

  return { date: input.dateIso.slice(0, 10), headline, items: capped };
}
